import { currencyFormatter, numberFormatterWithCommas, percents } from '@monorepo/tools/src/lib/utils/number';
import { TableLink } from '@monorepo/base/src/components/table/table-link/table-link';
import { TableMergeColumn } from '@monorepo/base/src/components/table/table-merge-column/table-merge-column';
import { isSubRow } from '@monorepo/base/src/components/table/table';
import { Tag } from '@monorepo/base/src/components/tag/tag';
import { CampaignModel, ICampaign } from '../../../models/campaign.model';
import { IPerformanceItem, PerformanceResponse } from '../../../models/performance.model';
import routes from '../../../routes/index.routes';
import { useCampaignActions } from '../../actions/campaign.actions';
import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import { IEntityHeaders, IMergedHeadersReturn, renderSubRow } from '../index.headers';
import { useWithPerformanceHeaders } from '../with-performance.headers';
import { IAdminxStore } from '../../../stores';
import { mapToTagStatus, Status, Statuses } from '../../../enums/status';
import { useRoute } from '@monorepo/tools/src/lib/hooks/tools/use-route';
import React, { useEffect } from 'react';
import { Icon } from '@monorepo/base/src/components/icon/icon';
import { SpacerX } from '@monorepo/base/src/components/spacer/spacer';
import { QuickActionsMenu } from '@monorepo/base/src/components/table/quick-actions-menu/quick-actions-menu';
import { TableSelectionHeader, TableSelectionCell } from '@monorepo/base/src/components/table/table-selection-cell/table-selection-cell';
import { TableStatusCell } from '../../../components/table-status-cell/table-status-cell';
import { createColumnHelper, sortingFns } from '@tanstack/react-table';
import { TableInlineEditCell } from '../../../components/table-inline-edit-cell/table-inline-edit-cell';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { id } from '@monorepo/tools/src/lib/types/primitives';
import { ActionModal } from '@monorepo/controlled/src/components/modal/modals/action-modal/action-modal';
import { BudgetInlineEditModal } from '../../../components/forms/campaign/budget/budget-inline-edit.modal';
import { MaxSalesInlineEditModal } from '../../../components/forms/campaign/daily-max-sales/max-sales-inline-edit.modal';
import styles from './campaigns.headers.module.scss';

const columnHelper = createColumnHelper<ICampaign & IPerformanceItem>();
const formatDailyMaxSalesCell = (dailyMaxSales: number | null | undefined): string => {
	if (dailyMaxSales == null) {
		return '';
	}
	if (dailyMaxSales < 0 || Number.isNaN(dailyMaxSales)) {
		return 'N/A';
	}
	return numberFormatterWithCommas.format(dailyMaxSales);
};

const sumDestinationDailyMaxSales = (destinations: { daily_max_sales?: number | null }[]): number | null => {
	if (!destinations.length) {
		return null;
	}

	let sum = 0;
	let hasValue = false;
	let hasNull = false;

	for (const link of destinations) {
		const value = link.daily_max_sales;
		if (value == null) {
			hasNull = true;
			continue;
		}
		if (value < 0 || Number.isNaN(value)) {
			return -1;
		}
		hasValue = true;
		sum += value;
	}

	if (!hasValue) {
		return null; // all null → empty
	}
	if (hasNull) {
		return -1; // partial → N/A
	}
	return sum; // all set → sum
};

export const useCampaignHeaders = (props: IEntityHeaders = {}): IMergedHeadersReturn<CampaignModel> => {
	const { includeColumns = {}, reportList, summary } = props;
	const { advertiser = true, campaign_group = true } = includeColumns;
	const { sidebarStore, settingsStore, campaignStore, modalsStore } = useStores<IAdminxStore>();
	const { EntityActions } = useCampaignActions();
	const { dispatchLog } = useLogs();
	const { columns: mergedPerforamnceHeaders } = useWithPerformanceHeaders({
		summaryItem: summary,
		withAuctionsValid: true,
	});
	const { currentRouteWithoutSpecialChars } = useRoute();
	const tableStore = settingsStore.pages.get(currentRouteWithoutSpecialChars)?.tableStore;

	useEffect(() => {
		if (tableStore && Object.keys(tableStore.getColumnVisibility()).length === 0) {
			tableStore.setDefaultColumnVisibility({
				name: true,
				status: true,
				advertiserName: true,
				advertiserId: false,
				campaignGroupName: true,
				campaignGroupId: true,
				dailyBudgetLimit: true,
				dailyMaxSales: true,
				id: true,
				date: false,
				auctions: true,
				auctionsValid: true,
				wins: true,
				clicks: true,
				conversions: true,
				cost: true,
				conversionValue: true,
				winRate: true,
				roas: true,
				ctr: true,
				lpCvr: true,
				impressions: false,
				optCost: false,
				optGain: false,
				gain: false,
				dailyBudget: true,
				spm: true,
				auctionType: true,
				minBidToWin: false,
				maxBidToWin: false,
				avgBidToWin: false,
				cpa: false,
				cpc: false,
				cpm: false,
			});
			tableStore?.setColumnVisibility(tableStore.getDefaultColumnVisibility());
		}
	}, [tableStore]);

	const renderHierachyColumns = () => {
		const columns = [];

		if (advertiser) {
			columns.push(
				columnHelper.accessor('advertiserName', {
					header: 'Advertiser',
					cell: info => {
						const { row } = info;
						if (!row.getValue('advertiserId')) {
							return <span>{row.getValue('advertiserName')}</span>;
						}
						return (
							<TableLink
								to={routes.advertisers.campaigns(row.getValue('advertiserId'))}
								onClick={() => {
									sidebarStore.setSidebarOpen(false);
								}}>
								{row.getValue('advertiserName')}
							</TableLink>
						);
					},
				})
			);
			columns.push(
				columnHelper.accessor('advertiserId', {
					header: 'Advertiser ID',
				})
			);
		}

		if (campaign_group) {
			columns.push(
				columnHelper.accessor('campaignGroupName', {
					header: 'Campaign Group',
					cell: info => {
						const { row } = info;

						if (!row.getValue('campaignGroupId')) {
							return <span>{row.getValue('campaignGroupName')}</span>;
						}
						return (
							<TableLink
								to={routes.campaignGroups.campaigns(row.getValue('campaignGroupId'))}
								onClick={() => sidebarStore.setSidebarOpen(false)}>
								{row.getValue('campaignGroupName')}
							</TableLink>
						);
					},
				})
			);
			columns.push(
				columnHelper.accessor('campaignGroupId', {
					header: 'Campaign Group ID',
				})
			);
		}

		return columns;
	};

	return {
		columns: [
			columnHelper.display({
				id: 'selection',
				enableHiding: false,
				header: headerProps => (
					<TableMergeColumn>
						<TableSelectionHeader headerProps={headerProps} />
						<SpacerX x={0.7} />
						<Icon size={'14px'} isMFP={true} color={'#c5c5c5'}>
							circle
						</Icon>
						<SpacerX x={0.7} />
					</TableMergeColumn>
				),
				cell: cellProps => {
					const { row } = cellProps;
					const campaign = new CampaignModel(row.original);
					const status = campaign.getStatus()?.trim() as Statuses;

					if (isSubRow(row)) {
						return <span></span>;
					}
					if (!EntityActions) {
						return null;
					}
					return (
						<TableMergeColumn>
							<TableSelectionCell cellProps={cellProps} />
							<SpacerX x={0.7} />
							<TableStatusCell status={status} />
							<QuickActionsMenu>
								<EntityActions entity={campaign} />
							</QuickActionsMenu>
						</TableMergeColumn>
					);
				},
			}),
			columnHelper.accessor('name', {
				header: 'Name',
				cell: info => {
					const { row } = info;
					if (isSubRow(row)) {
						return renderSubRow({ row });
					}
					return (
						<TableLink
							to={routes.campaigns.view(row.getValue('id'))}
							toEdit={routes.campaigns.edit(row.getValue('id'))}
							onClick={() => {
								sidebarStore.setSidebarOpen(false);
							}}>
							{row.getValue('name')}
						</TableLink>
					);
				},
			}),
			columnHelper.accessor('status', {
				header: 'Status',
				cell: info => {
					const { row } = info;
					if (isSubRow(row)) {
						return <span></span>;
					}
					const status = row.getValue('status') as string | undefined;
					return <Tag type={mapToTagStatus(status?.toUpperCase().trim() as Status)}>{status?.trim()}</Tag>;
				},
			}),
			columnHelper.accessor('id', {
				header: 'ID',
			}),
			columnHelper.accessor(row => row?.dailyBudget, {
				id: 'dailyBudget',
				header: 'Budget',
				enableResizing: false,
				cell: ({ row }) => {
					if (isSubRow(row)) {
						return <span></span>;
					}

					return (
						<TableInlineEditCell
							value={currencyFormatter.format(row.getValue('dailyBudget'))}
							inputValue={row.getValue('dailyBudget')}
							inputWidth={70}
							onEdit={() => {
								modalsStore.addModal({
									children: (
										<ActionModal
											className={styles.actionModal}
											childrenWrapperClassName={styles.actionModalChildrenWrapper}
											primaryBtnTitle="Save"
											onAction={async () => {
												dispatchLog({
													msg: `Updating campaign ${row.getValue('name')}...`,
													type: DispatchLogsTypes.Info,
												});

												const id: id = row.getValue('id');

												const dailyBudget = campaignStore
													.getCrud()
													.getEditFormData()
													?.campaign_phases?.filter(p => p.active)
													?.reduce((acc, i) => acc + (i?.daily_budget?.limit || 0), 0);

												await campaignStore.getCrud().edit();

												reportList?.setData({
													...reportList?.getData(),
													data: reportList?.getData()?.data?.map(r => {
														if (r.id !== id) {
															return r;
														}

														return { ...r, dailyBudget };
													}),
												} as PerformanceResponse);

												dispatchLog({
													msg: `Campaign ${row.getValue('name')} was successfully edited`,
													type: DispatchLogsTypes.Success,
												});

												campaignStore.getCrud().reset();
											}}>
											<BudgetInlineEditModal id={row.getValue('id')} />
										</ActionModal>
									),
								});
							}}
							onSave={async v => {
								console.log('ON_SAVE => ', v);
							}}
						/>
					);
				},
			}),
			columnHelper.accessor(row => row?.dailyMaxSales, {
				id: 'dailyMaxSales',
				header: 'Max Sales',
				enableResizing: false,
				cell: ({ row }) => {
					if (isSubRow(row)) {
						return <span></span>;
					}
					const dailyMaxSales = row.getValue('dailyMaxSales') as number | null | undefined;

					return (
						<TableInlineEditCell
							value={formatDailyMaxSalesCell(dailyMaxSales)}
							inputValue={dailyMaxSales != null && dailyMaxSales >= 0 ? String(dailyMaxSales) : ''}
							inputWidth={70}
							onEdit={() => {
								modalsStore.addModal({
									children: (
										<ActionModal
											className={styles.actionModal}
											childrenWrapperClassName={styles.actionModalChildrenWrapper}
											primaryBtnTitle="Save"
											onAction={async () => {
												dispatchLog({
													msg: `Updating campaign ${row.getValue('name')}...`,
													type: DispatchLogsTypes.Info,
												});
												const id: id = row.getValue('id');
												const dailyMaxSales = sumDestinationDailyMaxSales(
													campaignStore.getCrud().getData().getCampaignDestinations()
												);
												// or null if every destination is empty — product choice
												await campaignStore.getCrud().edit();
												reportList?.setData({
													...reportList?.getData(),
													data: reportList?.getData()?.data?.map(r => {
														if (r.id !== id) {
															return r;
														}
														return { ...r, dailyMaxSales };
													}),
												} as PerformanceResponse);
												dispatchLog({
													msg: `Campaign ${row.getValue('name')} was successfully edited`,
													type: DispatchLogsTypes.Success,
												});
												campaignStore.getCrud().reset();
											}}>
											<MaxSalesInlineEditModal id={row.getValue('id')} />
										</ActionModal>
									),
								});
							}}
							onSave={async () => {
								// modal handles save
							}}
						/>
					);
				},
			}),
			columnHelper.accessor('spm', {
				header: 'SPM',
				cell: info => {
					const { row } = info;
					if (isSubRow(row)) {
						return <span></span>;
					}
					return <span>{row.getValue('spm')}</span>;
				},
			}),
			columnHelper.accessor('minBidToWin', {
				header: 'Min Bid To Win',
				cell: ({ row }) => <span>{numberFormatterWithCommas.format(row.getValue('minBidToWin')) ?? '-'}</span>,
				footer: () => <span>{numberFormatterWithCommas.format(summary?.minBidToWin || 0)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),

			columnHelper.accessor('maxBidToWin', {
				header: 'Max Bid To Win',
				cell: ({ row }) => <span>{numberFormatterWithCommas.format(row.getValue('maxBidToWin')) ?? '-'}</span>,
				footer: () => <span>{numberFormatterWithCommas.format(summary?.maxBidToWin || 0)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),

			columnHelper.accessor('avgBidToWin', {
				header: 'Avg Bid To Win',
				cell: ({ row }) => <span>{numberFormatterWithCommas.format(row.getValue('avgBidToWin')) ?? '-'}</span>,
				footer: () => <span>{numberFormatterWithCommas.format(summary?.avgBidToWin || 0)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),

			columnHelper.accessor('cpa', {
				header: 'CPA',
				cell: ({ row }) => <span>{percents(row.getValue('cpa')) ?? '-'}</span>,
				footer: () => <span>{percents(summary?.cpa)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),

			columnHelper.accessor('cpc', {
				header: 'CPC',
				cell: ({ row }) => <span>{percents(row.getValue('cpc')) ?? '-'}</span>,
				footer: () => <span>{percents(summary?.cpc)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),
			columnHelper.accessor('cpm', {
				header: 'CPM',
				cell: ({ row }) => <span>{percents(row.getValue('cpm')) ?? '-'}</span>,
				footer: () => <span>{percents(summary?.cpm)}</span>,
				sortingFn: (rowA, rowB, columnId) => sortingFns.alphanumeric(rowA, rowB, columnId),
				meta: {
					isPerformance: true,
				},
			}),
			columnHelper.accessor('auctionType', {
				header: 'Auction Type',
				cell: info => {
					const { row } = info;
					return <span>{row.getValue('auctionType')}</span>;
				},
			}),
			...renderHierachyColumns(),
			...mergedPerforamnceHeaders,
		],
	};
};
